/**
 * Loading a Team Member's profile, which comes in two parts: their own details
 * (qualifications, earlier roles, professional context) and their Eduvos career
 * history from F&O. One part failing must not hide the other: when the details
 * failed, the F&O history wasn't shown either and the page said only "Server
 * error" (HR UAT list #8, bridge #110).
 */

export interface ProfileParts<Details, Career> {
  details: Details | null
  career: Career | null
  /** What couldn't load, in plain words; null when both parts loaded. */
  problem: string | null
}

/** Load both parts side by side; a part that fails comes back as null. */
export async function loadProfileParts<Details, Career>(
  loadDetails: () => Promise<Details>,
  loadCareer: () => Promise<Career>,
): Promise<ProfileParts<Details, Career>> {
  const [details, career] = await Promise.allSettled([loadDetails(), loadCareer()])
  if (details.status === 'rejected') console.warn('Failed to load profile details:', details.reason)
  if (career.status === 'rejected') console.warn('Failed to load internal career history:', career.reason)
  return {
    details: details.status === 'fulfilled' ? details.value : null,
    career: career.status === 'fulfilled' ? career.value : null,
    problem: profileProblem(details.status === 'rejected', career.status === 'rejected'),
  }
}

/** Says which part of the profile couldn't load, and that the rest is shown. */
export function profileProblem(detailsFailed: boolean, careerFailed: boolean): string | null {
  if (detailsFailed && careerFailed) {
    return "Your profile couldn't be loaded just now. Please try again in a moment."
  }
  if (detailsFailed) {
    return "Your qualifications, earlier roles and professional details couldn't be loaded just now. Your Eduvos career history is shown."
  }
  if (careerFailed) {
    return "Your Eduvos career history couldn't be loaded just now. Your other profile details are shown."
  }
  return null
}
